import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "typeRadio", "typeHidden", "tableSelect", "columnSelect",
    "simpleFields", "computedFields", "filterFields",
    "aggregateWrapper", "definitionJson",
    "formulaInput", "tablesInput", "filterInput",
    "chipContainer", "filterChipContainer",
    "previewText"
  ]
  static values = { columns: Array }

  connect() {
    this.columnsCache = {}
    // Cache initial columns if provided
    const tableId = this.tableSelectTarget.value
    if (tableId && this.columnsValue.length) {
      this.columnsCache[tableId] = this.columnsValue
    }

    // Sync type hidden field from radio
    this.syncTypeHidden()

    // Sync on form submit
    this.element.addEventListener("submit", () => this.buildJson())
  }

  // --- Type selection ---
  selectType(event) {
    // Small delay to let radio update
    requestAnimationFrame(() => {
      this.syncTypeHidden()
      this.updateVisibleFields()
    })
  }

  syncTypeHidden() {
    const checked = this.element.querySelector('input[name="chat_business_concept[concept_type]"]:checked')
    if (checked && this.hasTypeHiddenTarget) {
      this.typeHiddenTarget.value = checked.value
    }
  }

  getSelectedType() {
    const checked = this.element.querySelector('input[name="chat_business_concept[concept_type]"]:checked')
    return checked?.value || "computed"
  }

  updateVisibleFields() {
    const type = this.getSelectedType()
    const isMetric    = type === "metric"
    const isDimension = type === "dimension"
    const isComputed  = type === "computed"
    const isFilter    = type === "filter_preset"

    if (this.hasSimpleFieldsTarget)   this.simpleFieldsTarget.classList.toggle("hidden", !(isMetric || isDimension))
    if (this.hasComputedFieldsTarget) this.computedFieldsTarget.classList.toggle("hidden", !isComputed)
    if (this.hasFilterFieldsTarget)   this.filterFieldsTarget.classList.toggle("hidden", !isFilter)
    if (this.hasAggregateWrapperTarget) this.aggregateWrapperTarget.classList.toggle("hidden", isDimension)
  }

  // --- Table changed: load columns ---
  tableChanged() {
    const tableId = this.tableSelectTarget.value
    if (!tableId) {
      this.clearColumns()
      return
    }

    // Auto-fill tables input for computed type
    if (this.hasTablesInputTarget) {
      const current = this.tablesInputTarget.value.trim()
      if (!current) this.tablesInputTarget.value = tableId
    }

    if (this.columnsCache[tableId]) {
      this.applyColumns(this.columnsCache[tableId])
      return
    }

    this.loadColumns(tableId)
  }

  loadColumns(tableId) {
    const orgId = new URLSearchParams(window.location.search).get("org_id")
    fetch(`/ai_admin/table_columns?org_id=${orgId}&table_id=${tableId}`, {
      headers: { "Accept": "application/json" }
    })
      .then(r => r.json())
      .then(columns => {
        this.columnsCache[tableId] = columns
        this.applyColumns(columns)
      })
      .catch(err => console.error('Failed to load columns:', err))
  }

  applyColumns(columns) {
    // Update column dropdown (for metric/dimension)
    if (this.hasColumnSelectTarget) {
      const current = this.columnSelectTarget.value
      let html = '<option value="">-- Select column --</option>'
      columns.forEach(col => {
        const selected = col.name === current ? " selected" : ""
        html += `<option value="${col.name}"${selected}>${col.name} (${col.type})</option>`
      })
      this.columnSelectTarget.innerHTML = html
    }

    // Update column chips (for computed formula)
    if (this.hasChipContainerTarget) {
      this.chipContainerTarget.innerHTML = columns.map(col =>
        `<button type="button" data-action="click->concept-form#insertColumn" data-col="${col.name}"
                 class="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-indigo-100 hover:text-indigo-700 transition cursor-pointer border border-gray-200">
           <span class="font-mono">${col.name}</span>
           <span class="text-gray-400 text-[10px]">${col.type}</span>
         </button>`
      ).join("")
    }

    // Update filter column chips
    if (this.hasFilterChipContainerTarget) {
      this.filterChipContainerTarget.innerHTML = columns.map(col =>
        `<button type="button" data-action="click->concept-form#insertFilterColumn" data-col="${col.name}"
                 class="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-indigo-100 hover:text-indigo-700 transition cursor-pointer border border-gray-200">
           <span class="font-mono">${col.name}</span>
         </button>`
      ).join("")
    }

    this.updatePreview()
  }

  clearColumns() {
    if (this.hasColumnSelectTarget) {
      this.columnSelectTarget.innerHTML = '<option value="">-- Select column --</option>'
    }
    if (this.hasChipContainerTarget) {
      this.chipContainerTarget.innerHTML = '<span class="text-xs text-gray-400 italic">Select a table to see columns</span>'
    }
    if (this.hasFilterChipContainerTarget) {
      this.filterChipContainerTarget.innerHTML = '<span class="text-xs text-gray-400 italic">Select a table to see columns</span>'
    }
  }

  // --- Insert helpers ---
  insertColumn(event) {
    event.preventDefault()
    const col = event.currentTarget.dataset.col
    if (this.hasFormulaInputTarget) {
      this.insertAtCursor(this.formulaInputTarget, col)
    }
  }

  insertFilterColumn(event) {
    event.preventDefault()
    const col = event.currentTarget.dataset.col
    if (this.hasFilterInputTarget) {
      this.insertAtCursor(this.filterInputTarget, col)
    }
  }

  insertSnippet(event) {
    event.preventDefault()
    const snippet = event.currentTarget.dataset.snippet.replace(/\\/g, " ")
    if (this.hasFormulaInputTarget) {
      this.insertAtCursor(this.formulaInputTarget, snippet)
    }
  }

  insertAtCursor(el, text) {
    const start = el.selectionStart
    const end = el.selectionEnd
    const before = el.value.substring(0, start)
    const after = el.value.substring(end)
    el.value = before + text + after
    el.selectionStart = el.selectionEnd = start + text.length
    el.focus()
  }

  // --- Preview ---
  updatePreview() {
    if (!this.hasPreviewTextTarget) return
    const field = this.hasColumnSelectTarget ? this.columnSelectTarget.value : ""
    const agg = this.element.querySelector('[name="chat_business_concept[definition_aggregate]"]')?.value || "sum"
    const table = this.tableSelectTarget.value || "table"
    const type = this.getSelectedType()

    if (field) {
      if (type === "dimension") {
        this.previewTextTarget.textContent = `GROUP BY ${field} from ${table}`
      } else {
        this.previewTextTarget.textContent = `${agg.toUpperCase()}(${field}) from ${table}`
      }
    } else {
      this.previewTextTarget.textContent = "Select a column to see the preview"
    }
  }

  // --- Build JSON on submit ---
  buildJson() {
    const type = this.getSelectedType()
    const tableName = this.tableSelectTarget.value
    let definition = {}

    if (type === "metric" || type === "dimension") {
      const field = this.hasColumnSelectTarget ? this.columnSelectTarget.value : ""
      definition = { table: tableName, field: field }
      if (type === "metric") {
        const agg = this.element.querySelector('[name="chat_business_concept[definition_aggregate]"]')?.value
        definition.aggregate = agg || "sum"
      } else {
        definition.group_by = field
        definition.display_name = field.replace(/_/g, " ")
      }
    } else if (type === "computed") {
      const formula = this.hasFormulaInputTarget ? this.formulaInputTarget.value : ""
      const tablesStr = this.hasTablesInputTarget ? this.tablesInputTarget.value : ""
      const tables = tablesStr ? tablesStr.split(",").map(t => t.trim()).filter(Boolean) : (tableName ? [tableName] : [])
      definition = { formula: formula, tables: tables }
    } else if (type === "filter_preset") {
      const condition = this.hasFilterInputTarget ? this.filterInputTarget.value : ""
      definition = { condition: condition }
    }

    if (this.hasDefinitionJsonTarget) {
      this.definitionJsonTarget.value = JSON.stringify(definition)
    }
  }
}
